/* eslint-disable @angular-eslint/component-selector */
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { BlinkDirective } from '@zero/shared/ui-directives';
import {
  DisplayLabelPipe,
  OrderExecTypePipe,
  OrderIsEditablePipe,
  OrderIsPartiallyFilledPipe,
  OrderStatusPipe,
  ShorthandSymbolPipe,
} from '@zero/shared/ui-pipes';
import { InstrumentDetailsLinkComponent, WatchlistToggleComponent } from '@zero/shared/ui-web-components';
import { OrderData, OrderStatus, Quote } from '@zero/shared/util-models';
import { differenceInCalendarDays } from 'date-fns';
import { OrderItemBase } from '../order-item-base/order-item.component';
import { OrderItemExecTypeComponent } from '../order-item-exec-type/order-item-exec-type.component';
import { OrderProvisionComponent } from '../order-provision/order-provision.component';
import { OrderValueComponent } from '../order-value/order-value.component';
import { StopLimitDataComponent } from '../stop-limit-data/stop-limit-data.component';

@Component({
  selector: 'tr[app-open-order-item]',
  templateUrl: './open-order-item.component.html',
  imports: [
    InstrumentDetailsLinkComponent,
    WatchlistToggleComponent,
    OrderItemExecTypeComponent,
    StopLimitDataComponent,
    BlinkDirective,
    RouterLink,
    NgClass,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    TranslocoPipe,
    DisplayLabelPipe,
    OrderStatusPipe,
    OrderIsPartiallyFilledPipe,
    OrderExecTypePipe,
    ShorthandSymbolPipe,
    OrderValueComponent,
    OrderProvisionComponent,
  ],
})
export class OpenOrderItemComponent extends OrderItemBase implements OnChanges {
  @Input() public isin2quote = new Map<string, Quote>();

  @Output() public cancelOrder = new EventEmitter<OrderData>();
  @Output() public editOrder = new EventEmitter<OrderData>();

  public isCancelable: boolean;
  public quote: number | undefined;
  public quoteTime: string | undefined;
  public isOldQuote: boolean | undefined;
  public isEditable: boolean;
  public quantity: number | undefined;

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (this.order) {
      this.isCancelable = this.isOrderCancelable(this.order);
      const pipe = new OrderIsEditablePipe();
      this.isEditable = pipe.transform(this.order);
    }

    this.isOldQuote = this.isQuoteOld(this.order) !== undefined ? this.isQuoteOld(this.order) : this.isOldQuote;
    this.quote = this.getQuote(this.order) ?? this.quote;
    this.quoteTime = this.getQuoteTime(this.order) ?? this.quoteTime;
    this.quantity = this.getQuantityByFixedRemainingAmount(this.order, this.quote) ?? this.order?.openQuantity;
  }

  private isOrderCancelable(order: OrderData): boolean {
    switch (order.status) {
      case OrderStatus.Created:
      case OrderStatus.Sent:
      case OrderStatus.Pending:
      case OrderStatus.Open:
      case OrderStatus.PartiallyFilled:
        return order.execType !== 'savings';
      default:
        return false;
    }
  }

  private getQuote(order: OrderData): number {
    if (order) {
      const quote4isin = this.isin2quote.get(order.isin);
      if (quote4isin) {
        if (order.direction === 'buy') {
          return quote4isin.ask;
        } else {
          return quote4isin.bid;
        }
      }
    }

    return undefined;
  }

  private getQuoteTime(order: OrderData): string {
    if (order) {
      const quote4isin = this.isin2quote.get(order.isin);
      if (quote4isin) {
        return quote4isin.time;
      }
    }
    return undefined;
  }

  private isQuoteOld(order: OrderData): boolean {
    const dateStr = this.getQuoteTime(order);
    if (!dateStr || dateStr.length < 3) {
      return false;
    }
    return differenceInCalendarDays(Date.now(), Date.parse(dateStr)) > 0;
  }

  private getQuantityByFixedRemainingAmount(order: OrderData, quote: number | undefined): number | undefined {
    if (!order || !quote) {
      return undefined;
    }

    if (!this.isFractionBuyOrderWithFixedRemainingAmount(order)) {
      return undefined;
    }

    return Math.round((order.fixedRemainingAmount / quote) * 1000) / 1000;
  }

  private isFractionBuyOrderWithFixedRemainingAmount(order: OrderData): boolean {
    if (!order) {
      return false;
    }
    const { fixedRemainingAmount, execType, direction } = order;
    return execType === 'fraction' && direction === 'buy' && !Number.isNaN(fixedRemainingAmount) && fixedRemainingAmount > 0;
  }
}
